import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { AuthService } from 'src/app/core/services/auth.service';
import { DeleteConfirmComponent } from './delete-confirm/delete-confirm.component';
import { MatDialog } from '@angular/material/dialog';

// notification.model.ts
export interface Notification {
  id: any
  sender: string
  message: string
  createdAt: Date
  isRead: boolean
  avatarUrl?: string
  avatarColor?: string
  attachment?: string
  displayTime?: string
  type?: string
  priority?: string | number
  profile_url?: string
  notify_type?: string  // 'offer' → Promotional, anything else → Primary
}

export interface ApiNotificationResponse {
  error: boolean
  data: any[]
  message?: string
}

export interface NotificationGroup {
  key: string
  value: Notification[]
  sortPriority: number
}

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.css'],
    standalone: false
})
export class NotificationsComponent {
  notifications: Notification[] = []
  selectedNotifications: Set<string> = new Set()
  loading = false
  error: string | null = null
  activeTab: 'primary' | 'promotional' = 'primary'

  private destroy$ = new Subject<void>()

  constructor(
    private auth: AuthService, private dialog: MatDialog,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    // Read optional ?tab=primary|promotional from sidebar sub-menu links
    this.route.queryParams.subscribe(params => {
      if (params['tab'] === 'promotional') {
        this.activeTab = 'promotional';
      } else if (params['tab'] === 'primary') {
        this.activeTab = 'primary';
      }
    });
    this.loadNotifications();
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  loadNotifications(): void {
    this.loading = true
    this.error = null

    // console.log("Loading notifications...")

    this.auth
      .get_notifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          // console.log(" Raw API Response:", res)
          this.loading = false

          if (res && res.error === false) {
            let notificationsData = []

            // Handle different response structures
            if (res.data && res.data.notifications) {
              notificationsData = res.data.notifications
            } else if (res.notifications) {
              notificationsData = res.notifications
            } else if (res.data && Array.isArray(res.data)) {
              notificationsData = res.data
            } else if (Array.isArray(res)) {
              notificationsData = res
            }

            // console.log(" Extracted notifications data:", notificationsData)
            this.notifications = this.transformNotifications(notificationsData)

            // Sort notifications by timestamp (newest first) - FIXED
            this.notifications.sort((a, b) => {
              const timeA = new Date(a.createdAt).getTime()
              const timeB = new Date(b.createdAt).getTime()
              return timeB - timeA // Newest first
            })

            // console.log("Final transformed notifications:", this.notifications)
            // console.log("Notifications count:", this.notifications.length)
          } else {
            this.error = res?.message || "Failed to load notifications"
            this.toastr.error(this.error || "Failed to load notifications")
            // console.error(" API Error:", this.error)
          }
        },
        error: (error) => {
          // console.error("HTTP Error:", error)
          this.loading = false
          this.error = "Failed to load notifications"
          this.toastr.error("Failed to load notifications")
        },
      })
  }

  private transformNotifications(apiNotifications: any[]): Notification[] {
    if (!Array.isArray(apiNotifications)) {
      // console.warn("API notifications is not an array:", apiNotifications)
      return []
    }

    return apiNotifications.map((notif, index) => {
      //console.log(`Transforming notification ${index + 1}:`, notif)

      const id = notif.id || notif._id || `notif_${index}_${Date.now()}`
      const message = notif.message || notif.content || notif.text || "No message"
      const timestamp = notif.timestamp || notif.createdAt || notif.created_at || new Date().toISOString()
      const sender = this.extractSenderFromMessage(message) || "System"
      const isRead = notif.status === "read" || notif.isRead || notif.read || false
      const profileUrl = notif.profile_url || notif.avatar || notif.avatarUrl || ""

      // Handle document expiration notifications
      let finalProfileUrl = profileUrl
      const finalSender = sender
      let attachment = notif.attachment

      if (this.isDocumentExpirationNotification(message)) {
        // For document expiration, use account holder's profile
        finalProfileUrl = this.getAccountHolderProfileUrl()
        attachment = this.extractDocumentAttachment(message)
      }

      const transformed = {
        id: String(id),
        message: message,
        timestamp: timestamp,
        priority: notif.priority ?? 'medium',
        status: notif.status || (isRead ? "read" : "unread"),
        profile_url: finalProfileUrl,
        sender: finalSender,
        createdAt: new Date(timestamp),
        isRead: isRead,
        avatarUrl: finalProfileUrl,
        avatarColor: this.generateAvatarColor(finalSender),
        type: notif.type || "info",
        attachment: attachment,
        displayTime: this.formatTime(new Date(timestamp)),
        // Preserve notify_type from API; temporary fallback: priority <= 5 → 'offer'
        notify_type: notif.notify_type || (Number(notif.priority) <= 5 ? 'offer' : 'primary'),
      }

      // console.log(`Transformed notification ${index + 1}:`, transformed)
      return transformed
    })
  }

  private isDocumentExpirationNotification(message: string): boolean {
    const patterns = [
      /your\s+shared\s+folder/i,
      /document\s+added/i,
      /document\s+updated/i,
      /has\s+been\s+updated\s+by/i,
    ]
    return patterns.some((pattern) => pattern.test(message))
  }


  isCaseInformationRequestNotification(message: string): boolean {
    if (!message) return false
    return /requested\s+for\s+case\s+information/i.test(message)
  }


  onNotificationClick(notification: Notification): void {
    if (!notification || !notification.message) {
      return
    }

    if (this.isCaseInformationRequestNotification(notification.message)) {
      this.markNotificationAsRead(notification)
      this.router.navigate(['/consumer/legal-information'])
    }
  }


  private markNotificationAsRead(notification: Notification): void {
    if (!notification || notification.isRead) {
      return
    }

    this.auth
      .read_notification([notification.id])
      .subscribe({
        next: (res: any) => {
          if (res && res.error === false) {
            this.auth.triggerUnreadCountUpdate() // triggers notification count
            this.notifications = this.notifications.map((notif) =>
              notif.id === notification.id ? { ...notif, isRead: true, status: "read" } : notif,
            )
          }
        },
        error: () => {
          // Silent: the user has already navigated away, so surfacing an
          // error toast here would be confusing rather than helpful.
        },
      })
  }

  private getAccountHolderProfileUrl(): string {
    // Try multiple possible keys for user profile URL
    return (
      localStorage.getItem("user_profile_url") ||
      localStorage.getItem("profile_url") ||
      localStorage.getItem("avatar_url") ||
      localStorage.getItem("user_avatar") ||
      ""
    )
  }

  // Add new method to get account holder name
  private getAccountHolderName(): string {
    // Try multiple possible keys for user name
    const name =
      localStorage.getItem("user_name") ||
      localStorage.getItem("username") ||
      localStorage.getItem("full_name") ||
      localStorage.getItem("name") ||
      localStorage.getItem("user_full_name") ||
      ""

    // console.log("👤 Account holder name:", name)
    return name
  }

  private extractDocumentAttachment(message: string): string {
    // Extract document name from message
    const docMatch = message.match(/document\s+([A-Za-z\s]+)/i)
    return docMatch ? docMatch[1].trim() : "Document"
  }

  private extractSenderFromMessage(message: string): string {
    // Extract sender name from message patterns - IMPROVED
    const patterns = [
      /^([A-Za-z\s&,.-]+),\s+a\s+professional/i, // "BG Associates, a professional firm"
      /with\s+([A-Za-z\s&,.-]+)\s+has\s+been/i, // "with Global Law Firm has been updated"
      /^([A-Za-z\s&,.-]+)\s+has\s+sent/i,
      /^([A-Za-z\s&,.-]+)\s+sent/i,
      /^([A-Za-z\s&,.-]+)\s+shared/i,
      /from\s+([A-Za-z\s&,.-]+)/i,
      /by\s+([A-Za-z\s&,.-]+)/i, // "updated by Jack Daniel"
    ]

    for (const pattern of patterns) {
      const match = message.match(pattern)
      if (match && match[1].trim() !== "") {
        return match[1].trim()
      }
    }

    // If no specific sender found, return empty string to avoid "System:"
    return ""
  }

  // ── Tab helpers ──────────────────────────────────────────────
  setActiveTab(tab: 'primary' | 'promotional'): void {
    this.activeTab = tab
    this.selectedNotifications.clear()
  }

  get primaryNotifications(): Notification[] {
    return this.notifications.filter(n => n.notify_type !== 'offer')
  }

  get promotionalNotifications(): Notification[] {
    return this.notifications.filter(n => n.notify_type === 'offer')
  }

  get activeNotifications(): Notification[] {
    return this.activeTab === 'promotional'
      ? this.promotionalNotifications
      : this.primaryNotifications
  }

  // Selection methods
  toggleSelection(id: string): void {
    // console.log("Toggling selection for ID:", id)
    if (this.selectedNotifications.has(id)) {
      this.selectedNotifications.delete(id)
    } else {
      this.selectedNotifications.add(id)
    }
    //console.log(" Selected notifications:", Array.from(this.selectedNotifications))
  }

  isSelected(id: string): boolean {
    return this.selectedNotifications.has(id)
  }

  hasSelectedNotifications(): boolean {
    return this.selectedNotifications.size > 0
  }

  getUnreadCount(): number {
    return this.activeNotifications.filter((n) => !n.isRead).length
  }

  getPrimaryUnreadCount(): number {
    return this.primaryNotifications.filter(n => !n.isRead).length
  }

  getPromotionalUnreadCount(): number {
    return this.promotionalNotifications.filter(n => !n.isRead).length
  }

  trackByNotificationId(index: number, notification: Notification): string {
    return notification.id
  }

  // Add trackBy function for date groups
  trackByDateGroup(index: number, dateGroup: NotificationGroup): string {
    return dateGroup.key
  }

  // FIXED: Return array instead of object to preserve order
  getNotificationsByDate(): NotificationGroup[] {
    const grouped: { [key: string]: Notification[] } = {}

    // Use only the notifications for the currently active tab
    this.activeNotifications.forEach((notification) => {
      const dateGroup = this.getDateGroup(notification.createdAt)
      if (!grouped[dateGroup]) {
        grouped[dateGroup] = []
      }
      grouped[dateGroup].push(notification)
    })

    // Sort notifications within each group by timestamp (newest first)
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => {
        const timeA = new Date(a.createdAt).getTime()
        const timeB = new Date(b.createdAt).getTime()
        return timeB - timeA // Newest first
      })
    })

    // Convert to array of NotificationGroup objects with sort priority
    const groupArray: NotificationGroup[] = Object.entries(grouped).map(([key, notifications]) => ({
      key,
      value: notifications,
      sortPriority: this.getDateGroupSortPriority(key, notifications[0].createdAt),
    }))

    // Sort groups by priority (lower number = higher priority = appears first)
    groupArray.sort((a, b) => a.sortPriority - b.sortPriority)

    // console.log("Sorted notification groups:", groupArray)
    return groupArray
  }

  // Helper method to assign sort priority to date groups
  private getDateGroupSortPriority(dateGroup: string, sampleDate: Date): number {
    const today = new Date()
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())

    switch (dateGroup) {
      case "Today":
        return 1
      case "Yesterday":
        return 2
      case "2 days ago":
        return 3
      case "3 days ago":
        return 4
      case "4 days ago":
        return 5
      case "5 days ago":
        return 6
      case "6 days ago":
        return 7
      default:
        // For actual dates, use the date itself for sorting (newer dates get lower priority numbers)
        const daysDiff = Math.floor((todayStart.getTime() - sampleDate.getTime()) / (1000 * 60 * 60 * 24))
        return 100 + daysDiff // This ensures older dates get higher numbers
    }
  }

  markAsRead(): void {
    if (this.selectedNotifications.size === 0) {
      //this.toastr.info("Please select notifications to mark as read")
      return
    } 
    else{
      const selectedIds = Array.from(this.selectedNotifications)
      this.auth
        .read_notification(selectedIds)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            if (res && res.error === false) {
              this.auth.triggerUnreadCountUpdate(); //triggers notification count

              this.toastr.success(`${selectedIds.length} notification(s) marked as read successfully`)
              this.selectedNotifications.clear()
              // Update local state
              this.notifications = this.notifications.map((notif) =>
                selectedIds.includes(notif.id) ? { ...notif, isRead: true, status: "read" } : notif,
              )
            } else {
              this.toastr.error(res?.message || "Failed to mark notifications as read")
            }
          },
          error: (error) => {
            // console.error("Mark as read error:", error)
            this.toastr.error("Failed to mark notifications as read")
          },
        })
    }
  }

  deleteNotifications(): void {
    if (this.selectedNotifications.size === 0) {
      //this.toastr.info('Please select notifications to delete')
      return
    }
    else{
      const selectedIds = Array.from(this.selectedNotifications)
      const dialogRef = this.dialog.open(DeleteConfirmComponent, {
        width: '400px',
        data: { count: selectedIds.length },
      })

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.auth
            .delete_notifications(selectedIds)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res: any) => {
                if (res && res.error === false) {
                  this.auth.triggerUnreadCountUpdate(); //triggers notification count
                  this.toastr.success(`${selectedIds.length} notification(s) deleted successfully`)
                  this.selectedNotifications.clear()
                  this.notifications = this.notifications.filter(n => !selectedIds.includes(n.id))
                } else {
                  this.toastr.error(res?.message || 'Failed to delete notifications')
                }
              },
              error: (err) => {
                //console.error('Delete error:', err)
                this.toastr.error('Failed to delete notifications')
              }
            })
        }
      })
    }
  }

  // deleteNotifications(): void {
  //   if (this.selectedNotifications.size === 0) {
  //     this.toastr.info("Please select notifications to delete")
  //     return
  //   }

  //   const selectedIds = Array.from(this.selectedNotifications)

  //   if (confirm(`Are you sure you want to delete ${selectedIds.length} notification(s)?`)) {
  //     this.auth
  //       .delete_notifications(selectedIds)
  //       .pipe(takeUntil(this.destroy$))
  //       .subscribe({
  //         next: (res: any) => {
  //           if (res && res.error === false) {
  //             this.toastr.success(`${selectedIds.length} notification(s) deleted successfully`)
  //             this.selectedNotifications.clear()
  //             // Remove from local state
  //             this.notifications = this.notifications.filter((notif) => !selectedIds.includes(notif.id))
  //           } else {
  //             this.toastr.error(res?.message || "Failed to delete notifications")
  //           }
  //         },
  //         error: (error) => {
  //           console.error("Delete error:", error)
  //           this.toastr.error("Failed to delete notifications")
  //         },
  //       })
  //   }
  // }

  // Helper methods

  private getDateGroup(date: Date): string {
    const today = new Date()
    const notificationDate = new Date(date)

    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    const notifStart = new Date(notificationDate.getFullYear(), notificationDate.getMonth(), notificationDate.getDate())

    const diffTime = todayStart.getTime() - notifStart.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return "Today"
    } else if (diffDays === 1) {
      return "Yesterday"
    } else if (diffDays === 2) {
      return "2 days ago"
    } else if (diffDays === 3) {
      return "3 days ago"
    } else if (diffDays === 4) {
      return "4 days ago"
    } else if (diffDays === 5) {
      return "5 days ago"
    } else if (diffDays === 6) {
      return "6 days ago"
    } else if (diffDays <= 30) {
      return `${diffDays} days ago`
    } else {
      // For older notifications, show the actual date
      return notificationDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: notificationDate.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
      })
    }
  }

  private generateAvatarColor(sender: string): string {
    const colors = ["#004d87"]

    // If no sender or sender is "System", use account holder name for color
    let nameForColor = sender
    if (!sender || sender === "System" || sender === "") {
      nameForColor = this.getAccountHolderName()
    }

    const index = nameForColor.length % colors.length
    return colors[index]
  }

  private formatTime(date: Date): string {
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 1) {
      return "Just now"
    } else if (diffMins < 60) {
      return `${diffMins}m ago`
    } else if (diffHours < 24) {
      return `${diffHours}h ago`
    } else if (diffDays < 7) {
      return `${diffDays}d ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  // Helper method to check if image URL is valid
  hasValidProfileImage(notification: Notification): boolean {
    const notificationImage = notification.avatarUrl || notification.profile_url

    // First check if notification has its own image
    if (notificationImage && notificationImage !== "") {
      return true
    }

    // If no sender or sender is "System", check for account holder image
    if (!notification.sender || notification.sender === "System" || notification.sender === "") {
      const accountHolderImage = this.getAccountHolderProfileUrl()
      // console.log("🔍 Account holder profile URL:", accountHolderImage)
      return !!(accountHolderImage && accountHolderImage !== "")
    }

    return false
  }

  // Helper method to get the appropriate profile image URL
  getProfileImageUrl(notification: Notification): string {
    const notificationImage = notification.avatarUrl || notification.profile_url

    // First try notification's own profile image
    if (notificationImage && notificationImage !== "") {
      //  console.log("📸 Using notification profile image:", notificationImage)
      return notificationImage
    }

    // If no sender or sender is "System", use account holder's profile image
    if (!notification.sender || notification.sender === "System" || notification.sender === "") {
      const accountHolderImage = this.getAccountHolderProfileUrl()
      //console.log("👤 Using account holder profile image:", accountHolderImage)
      if (accountHolderImage && accountHolderImage !== "") {
        return accountHolderImage
      }
    }

    // console.log(" No profile image found")
    return ""
  }

  // Helper method to get initials when no profile image
  getInitials(name: string): string {
    // If no sender name or sender is "System", use account holder's name
    if (!name || name === "System" || name === "") {
      const accountHolderName = this.getAccountHolderName()
      // console.log(" Getting initials for account holder:", accountHolderName)
      if (accountHolderName && accountHolderName !== "") {
        return accountHolderName.charAt(0).toUpperCase()
      }
      return "U" // Only if no account holder name is found
    }

    // Handle company names - take first letter of first word
    const words = name.trim().split(" ")

    // For company names like "BG Associates", "Global Law Firm", etc.
    if (words.length >= 1) {
      return words[0].charAt(0).toUpperCase()
    }

    return name.charAt(0).toUpperCase()
  }

  // Add this method to handle image loading errors with account holder fallback
  onImageError(event: Event, notification: Notification): void {
    const imgElement = event.target as HTMLImageElement
    const fallbackElement = document.getElementById("fallback-" + notification.id)

    if (imgElement && fallbackElement) {
      // Try account holder profile URL if notification image fails
      const accountHolderImage = this.getAccountHolderProfileUrl()
      const currentSrc = imgElement.src

      // If we haven't tried account holder image yet and it exists
      if (accountHolderImage && accountHolderImage !== "" && currentSrc !== accountHolderImage) {
        imgElement.src = accountHolderImage
        return
      }

      // If all images fail, show initials
      imgElement.style.display = "none"
      fallbackElement.style.display = "flex"
    }
  }

  // Helper method to check if sender should be displayed
  shouldShowSender(sender: string): boolean {
    return !!sender && sender !== "" && sender !== "System"
  }
}